import React, { useState, useEffect } from 'react';
import { TrendingUp, Clock, Award, BarChart3, Calendar, RefreshCw, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import apiClient from '../../utils/apiClient';

import PageContainer from '../layout/PageContainer';
interface PopularMenuItem {
  rank: number;
  menu_id: string;
  menu_name: string;
  total_quantity: number;
  order_count: number;
  total_sales: number;
  average_per_order: string;
}

interface TimeSlotData {
  time_slot: string;
  hour: number;
  order_count: number;
  total_sales: number;
  avg_order_value: number;
  popular_days: string;
  peak_indicator: string;
}

interface Summary {
  today: { orders: number; sales: number };
  yesterday: { orders: number; sales: number };
  week: { orders: number; sales: number; daily_average: number };
  average_order_value: number;
  change_rate: number;
  trend: string;
}

const BentoAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const [popularMenu, setPopularMenu] = useState<PopularMenuItem[]>([]);
  const [timeSlots, setTimeSlots] = useState<any>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [dailyTrend, setDailyTrend] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(30);


  useEffect(() => {
    fetchAllData();
  }, [selectedPeriod]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // 並列でデータ取得
      const [popularRes, timeSlotsRes, summaryRes, trendRes] = await Promise.all([
        apiClient.get(`/takeout/analytics/popular-menu?days=${selectedPeriod}`),
        apiClient.get(`/takeout/analytics/time-slots?days=${selectedPeriod}`),
        apiClient.get(`/takeout/analytics/summary`),
        apiClient.get(`/takeout/analytics/daily-trend?days=14`)
      ]);

      setPopularMenu(popularRes.data.ranking || []);
      setTimeSlots(timeSlotsRes.data);
      setSummary(summaryRes.data.summary);
      setDailyTrend(trendRes.data.trend || []);
    } catch (error) {
      console.error('データ取得エラー:', error);
    }
    setLoading(false);
  };

  // グラフ用の色設定
  const COLORS = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe'];

  // ランキングメダルの表示
  const getRankBadge = (rank: number) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `${rank}位`;
    }
  };

  return (
    <PageContainer>
      <div className="space-y-6">
      {/* ヘッダー */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">📊 弁当予約分析</h2>
            <p className="text-gray-600 mt-1">注文データの詳細分析</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/home')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Home className="w-5 h-5" />
              ホーム
            </button>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(Number(e.target.value))}
              className="px-4 py-2 border rounded-lg"
            >
              <option value={7}>過去7日</option>
              <option value={14}>過去14日</option>
              <option value={30}>過去30日</option>
              <option value={90}>過去90日</option>
            </select>
            <button
              onClick={fetchAllData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              更新
            </button>
          </div>
        </div>

        {/* サマリーカード */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 rounded-lg text-white">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm opacity-90">本日の注文</span>
                <TrendingUp className="w-5 h-5" />
              </div>
              <div className="text-2xl font-bold">{summary.today.orders}件</div>
              <div className="text-sm opacity-90">¥{summary.today.sales.toLocaleString()}</div>
              <div className="text-xs mt-1">
                前日比: {summary.change_rate > 0 ? '+' : ''}{summary.change_rate}% {summary.trend}
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-4 rounded-lg text-white">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm opacity-90">今週の注文</span>
                <Calendar className="w-5 h-5" />
              </div>
              <div className="text-2xl font-bold">{summary.week.orders}件</div>
              <div className="text-sm opacity-90">日平均: {summary.week.daily_average}件</div>
            </div>

            <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 rounded-lg text-white">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm opacity-90">平均注文額</span>
                <BarChart3 className="w-5 h-5" />
              </div>
              <div className="text-2xl font-bold">¥{summary.average_order_value.toLocaleString()}</div>
              <div className="text-sm opacity-90">過去7日間</div>
            </div>

            <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 rounded-lg text-white">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm opacity-90">ピークタイム</span>
                <Clock className="w-5 h-5" />
              </div>
              <div className="text-2xl font-bold">
                {timeSlots?.peak_time?.time_slot || '-'}
              </div>
              <div className="text-sm opacity-90">
                {timeSlots?.peak_time?.order_count || 0}件の注文
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 人気メニューランキング */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <Award className="w-5 h-5 text-yellow-500" />
          人気メニューランキング TOP10
        </h3>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* ランキングリスト */}
            <div className="space-y-3">
              {popularMenu.map((item) => (
                <div key={item.menu_id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                  <div className="text-2xl font-bold" style={{ minWidth: '40px' }}>
                    {getRankBadge(item.rank)}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold">{item.menu_name}</div>
                    <div className="text-sm text-gray-600">
                      販売数: {item.total_quantity}個 | 
                      注文回数: {item.order_count}回 | 
                      平均: {item.average_per_order}個/回
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-lg">¥{item.total_sales.toLocaleString()}</div>
                    <div className="text-xs text-gray-500">売上</div>
                  </div>
                </div>
              ))}
            </div>

            {/* 円グラフ */}
            <div>
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={popularMenu.slice(0, 5)}
                    dataKey="total_quantity"
                    nameKey="menu_name"
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    label={(entry) => `${entry.menu_name}: ${entry.total_quantity}個`}
                  >
                    {popularMenu.slice(0, 5).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* 時間帯別注文分析 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-blue-500" />
          時間帯別注文分析
        </h3>
        
        {timeSlots && timeSlots.time_slots && (
          <>
            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                ピークタイム: <strong>{timeSlots.peak_time?.time_slot}</strong> | 
                総注文数: <strong>{timeSlots.total_orders}件</strong> | 
                総売上: <strong>¥{timeSlots.total_sales?.toLocaleString()}</strong>
              </p>
            </div>

            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={timeSlots.time_slots}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time_slot" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="order_count" fill="#667eea" name="注文数" />
              </BarChart>
            </ResponsiveContainer>

            {/* 時間帯詳細テーブル */}
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">時間帯</th>
                    <th className="px-4 py-2 text-center">状況</th>
                    <th className="px-4 py-2 text-right">注文数</th>
                    <th className="px-4 py-2 text-right">売上</th>
                    <th className="px-4 py-2 text-right">平均単価</th>
                    <th className="px-4 py-2 text-left">人気曜日</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {timeSlots.time_slots.map((slot: TimeSlotData) => (
                    <tr key={slot.time_slot} className="hover:bg-gray-50">
                      <td className="px-4 py-2">{slot.time_slot}</td>
                      <td className="px-4 py-2 text-center text-xl">{slot.peak_indicator}</td>
                      <td className="px-4 py-2 text-right font-semibold">{slot.order_count}件</td>
                      <td className="px-4 py-2 text-right">¥{slot.total_sales.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right">¥{slot.avg_order_value.toLocaleString()}</td>
                      <td className="px-4 py-2">{slot.popular_days || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* 日別推移グラフ */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-500" />
          過去14日間の注文推移
        </h3>
        
        {dailyTrend.length > 0 && (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={[...dailyTrend].reverse()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="order_date" 
                tickFormatter={(date) => new Date(date).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}
              />
              <YAxis />
              <Tooltip 
                labelFormatter={(date) => new Date(date).toLocaleDateString('ja-JP')}
                formatter={(value: any) => [`${value}件`, '注文数']}
              />
              <Line 
                type="monotone" 
                dataKey="order_count" 
                stroke="#667eea" 
                strokeWidth={2}
                dot={{ fill: '#667eea' }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
    </PageContainer>
  );
};

export default BentoAnalytics;